import React from 'react';
import { TestimonialCard } from './TestimonialCard';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
export function ProblemSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <div ref={ref} className="grain-section w-full px-6 py-32 md:py-40 bg-page-bg relative" data-id="element-260">
      {/* Chapter indicator */}
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={inView ? {
      opacity: 1,
      x: 0
    } : {}} transition={{
      duration: 0.8
    }} className="max-w-5xl mx-auto mb-24" data-id="element-261">
        <div className="flex flex-col md:flex-row md:items-center gap-8 mb-8" data-id="element-262">
          <div className="text-8xl md:text-9xl font-black text-black/5 font-sans tracking-tighter" data-id="element-263">
            01
          </div>
          <div className="h-px w-24 bg-black/10 hidden md:block" data-id="element-264"></div>
          <div data-id="element-265">
            <p className="text-xs text-teal-600 font-bold tracking-widest uppercase mb-3" data-id="element-266">
              Chapter One
            </p>
            <h2 className="text-4xl md:text-6xl font-bold text-ink tracking-tight" data-id="element-267">
              Why You're Still Stuck <br data-id="element-268" />
              <span className="text-teal-600" data-id="element-269">(And It's Not Your Fault)</span>
            </h2>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto space-y-20" data-id="element-270">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="space-y-8 text-xl md:text-2xl text-ink-light leading-relaxed font-light" data-id="element-271">
          <p className="font-medium text-ink" data-id="element-272">Let me guess.</p>
          <p data-id="element-273">You've been doing everything you're supposed to.</p>
          <p data-id="element-274">
            You've seen the specialists. Tried the diets. Taken the supplements.
            Read the articles, listened to the podcasts, followed the protocols.
          </p>
          <p data-id="element-275">
            You're constantly researching... Constantly trying new things...
          </p>

          <div className="bg-white p-10 rounded-3xl shadow-sm border-l-4 border-teal-500 my-10" data-id="element-276">
            <p className="text-2xl font-medium text-ink" data-id="element-277">
              But despite all that effort? You're still stuck in the same damn
              place. Chasing symptoms that just keep bouncing around like a game
              of whack-a-mole.
            </p>
          </div>

          <p data-id="element-278">
            And here's what makes it even MORE frustrating... You KNOW
            something's not right. You can feel it. But you don't know what the
            "right things" even ARE. Because nobody's been able to tell you.
          </p>

          <p data-id="element-279">
            Here's why:{' '}
            <span className="font-bold text-ink" data-id="element-280">
              Most practitioners don't actually know how to FIX things.
            </span>{' '}
            They know how to MANAGE symptoms. They know how to prescribe meds
            that cover up problems.
          </p>

          <p data-id="element-281">
            But when it comes to actually DIAGNOSING the root cause... And
            creating a PRECISE plan to fix it at the cellular level... They're
            lost. Because they were never trained to think that way.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="space-y-12 pt-16 border-t border-black/5" data-id="element-282">
          <h3 className="text-3xl md:text-5xl font-bold text-ink tracking-tight leading-tight" data-id="element-283">
            The Real Problem Isn't Lack of Effort... <br data-id="element-284" />
            <span className="text-teal-600" data-id="element-285">It's Lack of PRECISION</span>
          </h3>

          <div className="space-y-8 text-xl text-ink-light leading-relaxed font-light" data-id="element-286">
            <p data-id="element-287">
              You know what kills me? When I see someone who's been busting
              their ass for YEARS... Trying protocol after protocol... Only to
              discover that 90% of what they've been doing... Was either
              pointless or making things worse.
            </p>
            <p data-id="element-288">
              Not because they're stupid. But because nobody took the time to
              actually UNDERSTAND their unique biology.
            </p>

            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0f172a] text-white p-12 rounded-[2.5rem] shadow-xl my-10" data-id="element-289">
              <p className="text-2xl md:text-3xl font-medium leading-relaxed" data-id="element-290">
                Your body isn't a collection of separate problems to be fixed.
                Your joint pain isn't separate from your brain fog. Everything
                is connected.
              </p>
            </div>

            <p data-id="element-291">
              And when you treat symptoms in isolation... You're basically
              playing medical whack-a-mole. One thing gets "better"... Another
              thing pops up. It's exhausting. And it's completely unnecessary.
            </p>

            <p data-id="element-292">
              Because when you work with someone who understands how your ENTIRE
              system operates... When they can see the underlying patterns
              everyone else has missed... Everything becomes clear. Suddenly
              you're not managing 12 different problems. You're solving ONE core
              issue.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} animate={inView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        delay: 0.7,
        duration: 0.8
      }} data-id="element-293">
          <TestimonialCard text="I 100% would put my name behind Ali & Brenden. I've had other health coaches in the past (most are MD's), and I must say, Brenden & Ali are FAR more in depth, systems-oriented, and rigorous than anything I've ever seen... In terms of level of positive impact on my life, they are likely in the top 5 most important people I've ever met." highlight={true} profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/jdxYRgYb1UEVWzFMDVbhMC/WilfredA.jpg" name="Wilfred A., Peptide Mastery Owner & Transformation Services Client" data-id="element-294" />
          <p className="text-center text-xl font-medium text-ink mt-8" data-id="element-295">
            That's what precision looks like. And it's what you're about to
            experience.
          </p>
        </motion.div>
      </div>
    </div>;
}